import { Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Button } from './Button';
import { Card } from './Card';
import { Typography } from './Typography';

export interface ErrorCardProps {
  title: string;
  description: string;
  retryLabel: string;
  onRetry: () => void;
  cancelLabel?: string;
  onCancel?: () => void;
  sx?: SxProps<Theme>;
}

const errorCardContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '16px',
  width: '100%',
} satisfies SxProps<Theme>;

const errorCardActionsSx = {
  display: 'flex',
  justifyContent: 'center',
  gap: 1,
} satisfies SxProps<Theme>;

export const ErrorCard = ({
  title,
  description,
  retryLabel,
  onRetry,
  cancelLabel,
  onCancel,
  sx,
}: ErrorCardProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: { xs: 'center', md: 'stretch' },
        justifyContent: { xs: 'center', md: 'flex-start' },
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: '644px',
          minHeight: '346px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          alignSelf: 'center',
          padding: { xs: '40px 32px', md: '16px' },
          ...sx,
        }}
      >
        <Box sx={errorCardContainerSx}>
          <Box>
            <Typography
              variant="h6"
              display="block"
              sx={{
                color: 'rgba(0, 0, 0, 0.87)',
                textAlign: 'center',
                fontFamily: theme.typography.fontFamily,
                fontSize: '20px',
                fontWeight: theme.typography.fontWeightMedium,
                lineHeight: '160%',
                letterSpacing: '0.15px',
                mb: '0px',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              display="block"
              sx={{
                color: theme.palette.text.secondary,
                textAlign: 'center',
                fontFamily: theme.typography.fontFamily,
                fontSize: '16px',
                fontWeight: theme.typography.fontWeightRegular,
                lineHeight: '150%',
                letterSpacing: '0.15px',
                mb: '0px',
              }}
            >
              {description}
            </Typography>
          </Box>
          <Box sx={errorCardActionsSx}>
            {onCancel && cancelLabel && (
              <Button
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
                onClick={onCancel}
              >
                {cancelLabel}
              </Button>
            )}
            <Button
              variant="contained"
              sx={{ textTransform: 'capitalize' }}
              onClick={onRetry}
            >
              {retryLabel}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default ErrorCard;
